'use client'

import { Spacer } from '@vinted/web-ui'
import { useContext } from 'react'

import { useSession } from '@marketplace-web/shared/session-data'
import { ShippingDetailsContext } from '@marketplace-web/item-page/item-page-data'
import { transformCurrencyAmountDto } from '@marketplace-web/currency/currency-data'
import { useAbTest } from '@marketplace-web/feature-flags/ab-tests-data'
import {
  EscrowFeesContext,
  EscrowFees,
  EscrowFeesModel,
} from '@marketplace-web/escrow-pricing/escrow-pricing-data'

import ServiceFeeIncludedTitle from '../ServiceFeeIncludedTitle'
import DiscountBadges from '../ItemPageDiscountBadges'
import { getPriceBreakdownPhotoThumbnail } from '../../utils/item-page'
import ItemCurrencyConversionNote from './ItemCurrencyConversionNote'
import ItemPriceHeading from './ItemPriceHeading'
import { ItemPagePricingPluginModel } from './types'
import BalanceDisplay from './BalanceDisplay'

type Props = {
  data: ItemPagePricingPluginModel
}

const ItemPagePricingPlugin = ({ data }: Props) => {
  const { user } = useSession()
  const currentUserId = user?.id
  const { shippingDetails } = useContext(ShippingDetailsContext)
  const { escrowFees } = useContext(EscrowFeesContext)
  const {
    itemPrice,
    offerPrice,
    totalItemPrice,
    serviceFee,
    itemId,
    isSellerBusiness,
    itemUserId,
    itemTitle,
    photos,
    conversion,
  } = data

  const discountBadgePlacementTestIsOn = useAbTest('discount_badge_placement_v1')?.variant === 'on'

  const isViewingSelf = itemUserId === currentUserId

  const transformedItemPrice = transformCurrencyAmountDto(itemPrice)
  const transformedTotalPrice = totalItemPrice ? transformCurrencyAmountDto(totalItemPrice) : null
  const transformedServiceFee = serviceFee ? transformCurrencyAmountDto(serviceFee) : null
  const itemPhotoSrc = getPriceBreakdownPhotoThumbnail(photos)
  const showServiceFeeIncludedTitle =
    !!shippingDetails?.price && !!(transformedServiceFee || escrowFees)

  const getEscrowFees = (): EscrowFeesModel | null => {
    if (escrowFees) {
      return escrowFees
    }

    if (transformedServiceFee) {
      return {
        itemVerification: null,
        electronicsVerification: null,
        shipping: null,
        buyerProtection: {
          discountPercentage: 0,
          finalPrice: transformedServiceFee,
          discount: { amount: '0', currencyCode: transformedServiceFee.amount },
          type: EscrowFees.BuyerProtection,
          isFree: Number(transformedServiceFee.amount) === 0,
          originalPrice: transformedServiceFee,
        },
      }
    }

    return null
  }

  return (
    <div className="details-list details-list--pricing">
      <div data-testid="item-sidebar-price-container">
        <ItemPriceHeading
          price={transformedItemPrice}
          offerPrice={offerPrice}
          isItemOwner={isViewingSelf}
        />
        {showServiceFeeIncludedTitle && (
          <ServiceFeeIncludedTitle
            itemId={itemId}
            itemPrice={offerPrice || transformedItemPrice}
            isItemOwner={isViewingSelf}
            totalItemPrice={transformedTotalPrice}
            serviceFee={transformedServiceFee}
            isSellerBusiness={isSellerBusiness}
            shouldTrimTitle={isViewingSelf}
            detailedBreakdown={{
              itemTitle,
              escrowFees: getEscrowFees(),
              itemPhotoSrc,
            }}
            showInclinePrice={false}
          />
        )}
        <BalanceDisplay currentUserId={currentUserId} isItemOwner={isViewingSelf} />
        {!!escrowFees && !discountBadgePlacementTestIsOn && (
          <DiscountBadges
            escrowFees={escrowFees}
            isSellerBusiness={isSellerBusiness}
            itemUserId={itemUserId}
            itemTitle={itemTitle}
            itemPhotoSrc={itemPhotoSrc}
            itemPrice={transformedItemPrice}
            itemId={itemId}
            topSpacer={<Spacer size="large" />}
            bottomSpacer={<Spacer size="regular" />}
          />
        )}
      </div>
      <div
        className="details-list__item details-list--badges"
        data-testid="item-sidebar-pricing-details-container"
      >
        {conversion && (
          <>
            <Spacer />
            <ItemCurrencyConversionNote />
          </>
        )}
      </div>
    </div>
  )
}

export default ItemPagePricingPlugin
